import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useOnboardingWizard } from "./OnboardingWizardProvider";
import {
  Trophy,
  User,
  Building2,
  MessageSquare,
  Upload,
  Star,
} from "lucide-react";

const achievementIcons: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  User,
  Building2,
  MessageSquare,
  Upload,
  Trophy,
};

export function AchievementToast() {
  const { t } = useTranslation();
  const { pendingCelebrations, consumeCelebration, getAchievement } =
    useOnboardingWizard();
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState<{
    achievementId: string;
    points: number;
  } | null>(null);

  useEffect(() => {
    if (pendingCelebrations.length === 0 || visible) return;

    const celebration = pendingCelebrations[0];
    setCurrent(celebration);
    setVisible(true);

    // Auto-dismiss after 3 seconds
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => {
        consumeCelebration();
        setCurrent(null);
      }, 500); // wait for exit animation
    }, 3000);

    return () => clearTimeout(timer);
  }, [pendingCelebrations, visible, consumeCelebration]);

  if (!current) return null;

  const achievement = getAchievement(current.achievementId);
  if (!achievement) return null;

  const Icon = achievementIcons[achievement.icon] || Star;

  return (
    <div
      className={`
        fixed bottom-6 left-1/2 -translate-x-1/2 z-50
        transition-all duration-500 ease-out
        ${visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}
      `}
    >
      <div className="flex items-center gap-3 px-5 py-3 bg-card border border-nara-gold/50 rounded-2xl shadow-lg shadow-nara-gold/10">
        <div className="w-10 h-10 rounded-xl bg-nara-gold/20 flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-nara-gold" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">
            {t(achievement.title)}
          </p>
          <p className="text-xs text-muted-foreground">
            +{achievement.points} XP
          </p>
        </div>
      </div>
    </div>
  );
}
